/**
 * Single source of truth for auth state + the magic-link auth wall.
 *
 * The provider owns:
 *   - user / isAnonymous / loading state
 *   - the auth modal (opened from anywhere via openAuthModal)
 *   - the SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED side effects
 *
 * Anything below the provider consumes via useAuth() or, for action
 * gating, useRequireAuth(). Page components no longer need to set up
 * their own onAuthStateChange listeners.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { identify, reset, track } from '../utils/analytics';
import { clearSentryUser, setSentryUser } from '../utils/sentry';
import { MagicLinkAuthForm } from '../components/AuthForm';
import { useFocusTrap } from '../lib/a11y';

export interface AuthModalOptions {
  /** Headline copy override (default: "Sign in to CommsGenie"). */
  headline?: string;
  /** Subhead copy override. */
  subhead?: string;
  /**
   * URL the magic link should redirect to after a successful sign-in.
   * Default: the page the modal was opened from, so requireAuth flows
   * land the user back where they tried to act. Pass an explicit
   * value to override (e.g. '/onboarding' for first-run signup).
   */
  emailRedirectTo?: string;
}

export interface AuthContextValue {
  /** Supabase user object when signed in, null otherwise. */
  user: any | null;
  /** True while the initial session check is in flight. */
  loading: boolean;
  /** True after the initial check resolves with no user. */
  isAnonymous: boolean;
  /** Open the auth modal, optionally with custom copy. */
  openAuthModal: (opts?: AuthModalOptions) => void;
  /** Close the auth modal without signing in. */
  closeAuthModal: () => void;
  /** Sign out. Auth listener handles the rest (navigate, analytics, etc). */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();

  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpts, setModalOpts] = useState<AuthModalOptions>({});

  // Latest navigate ref so the auth listener (registered once on mount)
  // always calls the current navigate function.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // ── Initial session check ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session?.user) {
          setUser(session.user);
          setIsAnonymous(false);
          identify(session.user.id, { is_anonymous: false });
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.full_name,
          });
        } else {
          setIsAnonymous(true);
        }
      } catch (err) {
        console.error('[auth] initial getSession failed:', err);
        if (!cancelled) setIsAnonymous(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Auth state listener ────────────────────────────────────────────
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    (async () => {
      const { data } = await supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (event === 'SIGNED_IN' && session?.user) {
            setUser(session.user);
            setIsAnonymous(false);
            setModalOpen(false);
            identify(session.user.id, { is_anonymous: false });
            setSentryUser({
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.full_name,
            });
            track('user_logged_in', { method: 'magic_link' });
            // Phase 3: tell any page-level listener that the user has
            // signed in and there may be a pending intent waiting to be
            // resumed. Pages call consumePendingAuthIntent() to check.
            // Dispatched on next tick so React has rendered the
            // authed UI first.
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('cg:auth-resume'));
            }, 0);
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setIsAnonymous(true);
            track('user_logged_out');
            reset();
            clearSentryUser();
            // Drop back to public landing so the user isn't stranded
            // on a personal route (e.g. /watchlist, /workspaces).
            navigateRef.current('/', { replace: true });
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            setUser(session.user);
            setIsAnonymous(false);
          }
        }
      );
      subscription = data?.subscription ?? null;
    })();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // ── Modal controls ─────────────────────────────────────────────────
  const openAuthModal = useCallback((opts?: AuthModalOptions) => {
    // Default redirect: bring the user back to whatever URL they were
    // on when the modal opened, so the Phase 3 auto-resume picks up
    // the action they tried to take. Caller can override (e.g. signup
    // flows that want /onboarding).
    const defaultRedirect =
      typeof window !== 'undefined'
        ? window.location.origin + window.location.pathname + window.location.search
        : undefined;
    setModalOpts({
      ...(opts ?? {}),
      emailRedirectTo: opts?.emailRedirectTo ?? defaultRedirect,
    });
    setModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Trap focus inside the auth dialog while open; Escape closes it and focus
  // returns to the trigger.
  const authDialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(authDialogRef, modalOpen, closeAuthModal);

  // ── Sign out ───────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      console.error('[auth] signOut failed:', err);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAnonymous,
      openAuthModal,
      closeAuthModal,
      signOut,
    }),
    [user, loading, isAnonymous, openAuthModal, closeAuthModal, signOut]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {modalOpen && (
        // Backdrop dismissal is a mouse convenience; keyboard users close via
        // Escape (focus trap) or the form's Cancel.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAuthModal();
          }}
        >
          <div
            ref={authDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Sign in to CommsGenie"
            className="w-full max-w-md rounded-2xl border border-green-200/30 bg-white/90 p-8 shadow-xl backdrop-blur-xl"
          >
            <MagicLinkAuthForm
              onCancel={closeAuthModal}
              headline={modalOpts.headline}
              subhead={modalOpts.subhead}
              emailRedirectTo={modalOpts.emailRedirectTo}
            />
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
